import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Stack,
  Button,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Alert,
  alpha,
  LinearProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { usePermissions } from '../../utils/permissions';
import { supabase } from '../../supabaseClient';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewListIcon from '@mui/icons-material/ViewList';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { HeaderCard, SectionPaper } from './accounting-overview-components/layout';
import PayablesSummary from './accounting-payables-components/PayablesSummary';
import PayablesFilters from './accounting-payables-components/PayablesFilters';
import PayablesTable from './accounting-payables-components/PayablesTable';
import PayablesCalendar from './accounting-payables-components/PayablesCalendar';
import PayableDetailDialog from './accounting-payables-components/PayableDetailDialog';
import MarkPaymentDialog from './accounting-payables-components/MarkPaymentDialog';
import MarkUnpaidDialog from './accounting-payables-components/MarkUnpaidDialog';

const AccountingPayablesPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { 
    canViewAccountingPayables,
    canEditAccountingPayables,
    canDeleteAccountingPayables
  } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [payables, setPayables] = useState([]);
  const [filteredPayables, setFilteredPayables] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [summaryStats, setSummaryStats] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'calendar'

  // Dialog states
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [unpaidDialogOpen, setUnpaidDialogOpen] = useState(false);
  const [selectedPayable, setSelectedPayable] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    searchQuery: '',
    paymentStatus: 'all',
    category: 'all',
    supplierId: 'all',
    fromDate: '',
    toDate: '',
    dueInDays: ''
  });

  // Define fetchCompanyId before it's used in useEffect
  const fetchCompanyId = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('No authenticated user found');
        return;
      }

      // First, try to get company from company_users table (for employees)
      const { data: companyUser, error: companyUserError } = await supabase
        .from('company_users')
        .select('company_id')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (companyUser?.company_id) {
        setCompanyId(companyUser.company_id);
        return;
      }

      // If not found, check if user is a company owner
      const { data: ownedCompany, error: ownedCompanyError } = await supabase
        .from('companies')
        .select('id')
        .eq('created_by', user.id)
        .maybeSingle();

      if (ownedCompany?.id) {
        setCompanyId(ownedCompany.id);
        return;
      }

      // If still not found, show warning
      console.warn('No company association found for user');
      setSnackbar({
        open: true,
        message: 'No company associated with your account',
        severity: 'warning'
      });
    } catch (error) {
      console.error('Error fetching company ID:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load company information',
        severity: 'error'
      });
    }
  };

  // Define applyFilters before it's used in useEffect
  const applyFilters = () => {
    let filtered = [...payables];

    // Search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.supplier_name?.toLowerCase().includes(query) ||
        p.invoice_number?.toLowerCase().includes(query) ||
        p.item_name?.toLowerCase().includes(query)
      );
    }

    // Payment status filter
    if (filters.paymentStatus !== 'all') {
      filtered = filtered.filter(p => 
        p.payment_status?.toLowerCase() === filters.paymentStatus.toLowerCase()
      );
    }

    // Category filter
    if (filters.category !== 'all') {
      if (filters.category.startsWith('expense_')) {
        // Filter by expense category ID
        const categoryId = filters.category.replace('expense_', '');
        filtered = filtered.filter(p => p.transaction_type === 'expense' && p.category_id === categoryId);
      } else {
        // Filter by inventory type
        filtered = filtered.filter(p => p.transaction_type === filters.category);
      }
    }

    // Supplier filter
    if (filters.supplierId !== 'all') {
      filtered = filtered.filter(p => p.supplier_id === filters.supplierId);
    }

    // Date range filter
    if (filters.fromDate) {
      filtered = filtered.filter(p => 
        !p.invoice_date || new Date(p.invoice_date) >= new Date(filters.fromDate)
      );
    }
    if (filters.toDate) {
      filtered = filtered.filter(p => 
        !p.invoice_date || new Date(p.invoice_date) <= new Date(filters.toDate)
      );
    }

    // Due in X days filter
    if (filters.dueInDays && filters.dueInDays > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const futureDate = new Date(today);
      futureDate.setDate(futureDate.getDate() + parseInt(filters.dueInDays));
      
      filtered = filtered.filter(p => {
        if (!p.invoice_due_date) return false;
        const dueDate = new Date(p.invoice_due_date);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate >= today && dueDate <= futureDate;
      });
    }

    setFilteredPayables(filtered);
  };

  // Define fetchData before it's used in useEffect
  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchPayables(),
        fetchSuppliers(),
        fetchCategories()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      setSnackbar({
        open: true,
        message: 'Error loading payables data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // All hooks must be called before any conditional returns
  useEffect(() => {
    fetchCompanyId();
  }, []);

  const fetchCompanyData = async () => {
    if (!companyId) return;
    try {
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      
      if (companyError) throw companyError;
      setCompanyName(companyData?.name || 'Company');
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchData();
      fetchCompanyData();
    }
  }, [companyId]);

  useEffect(() => {
    applyFilters();
  }, [filters, payables]);

  // Permission check - after all hooks
  if (!canViewAccountingPayables()) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <CreditScoreIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Payables Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Payables. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  const fetchPayables = async () => {
    try {
      // Fetch from all transaction types
      const tables = [
        { name: 'bottle_transactions', type: 'bottles', itemTable: 'bottles', itemField: 'bottle_id' },
        { name: 'caps_transactions', type: 'caps', itemTable: 'caps', itemField: 'cap_id' },
        { name: 'label_transactions', type: 'labels', itemTable: 'labels', itemField: 'label_id' },
        { name: 'raw_materials_transactions', type: 'raw_materials', itemTable: 'raw_materials', itemField: 'raw_material_id' },
        { name: 'supply_transactions', type: 'supplies', itemTable: 'supplies', itemField: 'supply_id' },
        { name: 'product_inventory_transactions', type: 'products', itemTable: 'products', itemField: 'product_id' }
      ];

      const allPayables = [];

      for (const table of tables) {
        let data, error;
        
        // For products, handle supplier separately due to multiple FK relationships
        if (table.type === 'products') {
          const result = await supabase
            .from(table.name)
            .select(`
              *,
              item:${table.itemTable}(name)
            `)
            .eq('company_id', companyId)
            .eq('transaction_type', 'purchase')
            .order('invoice_due_date', { ascending: true });
          
          data = result.data;
          error = result.error;
          
          // Manually fetch supplier names for products
          if (data && data.length > 0) {
            const supplierIds = [...new Set(data.map(t => t.supplier_id).filter(Boolean))];
            if (supplierIds.length > 0) {
              const { data: suppliers } = await supabase
                .from('suppliers')
                .select('id, name')
                .in('id', supplierIds);
              
              const supplierMap = suppliers?.reduce((acc, s) => {
                acc[s.id] = s;
                return acc;
              }, {}) || {};
              
              data = data.map(t => ({
                ...t,
                supplier: t.supplier_id ? supplierMap[t.supplier_id] : null
              }));
            }
          }
        } else {
          const result = await supabase
            .from(table.name)
            .select(`
              *,
              supplier:suppliers(name),
              item:${table.itemTable}(name)
            `)
            .eq('company_id', companyId)
            .eq('transaction_type', 'purchase')
            .order('invoice_due_date', { ascending: true });
          
          data = result.data;
          error = result.error;
        }

        if (error) {
          console.error(`Error fetching ${table.type}:`, error);
          continue;
        }

        if (data) {
          const mapped = data.map(transaction => {
            // Handle different field names across tables
            const isRawMaterial = table.type === 'raw_materials';
            const isProduct = table.type === 'products';
            
            // Get quantity based on table type
            let quantity;
            if (isRawMaterial) {
              quantity = transaction.unit_quantity;
            } else if (isProduct) {
              quantity = transaction.quantity_change || transaction.input_units;
            } else {
              quantity = transaction.quantity;
            }
            
            const subtotal = isRawMaterial && transaction.total_cost 
              ? transaction.total_cost 
              : (quantity || 0) * (transaction.unit_cost || 0);
            const shipping = transaction.shipping_cost || transaction.shipping_amount || 0;
            const tax = transaction.tax_amount || 0;
            const totalAmount = subtotal + tax + shipping;

            return {
              transaction_id: transaction.id,
              transaction_type: table.type,
              company_id: transaction.company_id,
              supplier_id: transaction.supplier_id,
              supplier_name: transaction.supplier?.name || 'Unknown Vendor',
              invoice_number: transaction.invoice_number,
              invoice_date: transaction.invoice_date,
              invoice_due_date: transaction.invoice_due_date,
              subtotal_amount: subtotal,
              tax_amount: tax,
              shipping_cost: shipping,
              total_amount: totalAmount,
              payment_status: transaction.payment_status || 'unpaid',
              paid_amount: transaction.paid_amount || 0,
              remaining_amount: totalAmount - (transaction.paid_amount || 0),
              invoice_file_path: transaction.invoice_file_path,
              invoice_file_name: transaction.invoice_file_name,
              item_name: transaction.item?.name || 'N/A',
              quantity: quantity,
              unit_cost: transaction.unit_cost,
              created_at: transaction.transaction_date || transaction.created_at,
              notes: transaction.notes 
            };
          });
          allPayables.push(...mapped);
        }
      }

      // Sort by due date
      allPayables.sort((a, b) => {
        if (!a.invoice_due_date) return 1;
        if (!b.invoice_due_date) return -1;
        return new Date(a.invoice_due_date) - new Date(b.invoice_due_date);
      });

      // Fetch expenses
      try {
        const { data: expensesData, error: expensesError } = await supabase
          .rpc('get_expenses_decrypted', { 
            expense_id_param: null,
            company_id_param: companyId 
          });

        if (!expensesError && expensesData) {
          const mappedExpenses = expensesData
            .filter(expense => expense.paid_by === 'By Company' && expense.status === 'approved')
            .map(expense => {
              const totalAmount = parseFloat(expense.total) || 0;
              const paidAmount = parseFloat(expense.paid_amount) || 0;

              return {
                transaction_id: expense.id,
                transaction_type: 'expense',
                category_id: expense.category_id,
                company_id: expense.company_id,
                supplier_id: null,
                supplier_name: 'Expense',
                invoice_number: expense.id.substring(0, 8),
                invoice_date: expense.expense_date,
                invoice_due_date: expense.expense_date,
                subtotal_amount: totalAmount,
                tax_amount: 0,
                shipping_cost: 0,
                total_amount: totalAmount,
                payment_status: expense.payment_status || 'unpaid',
                paid_amount: paidAmount,
                remaining_amount: totalAmount - paidAmount,
                invoice_file_path: expense.document_file_path,
                invoice_file_name: expense.document_file_name,
                item_name: expense.description || 'Expense',
                quantity: 1,
                unit_cost: totalAmount,
                created_at: expense.expense_date,
                notes: expense.payment_notes || expense.approval_notes
              };
            });
          allPayables.push(...mappedExpenses);
        }
      } catch (expenseError) {
        console.error('Error fetching expenses:', expenseError);
      }

      setPayables(allPayables);
      calculateSummaryStats(allPayables);
    } catch (error) {
      console.error('Error fetching payables:', error);
      throw error;
    }
  };

  const fetchSuppliers = async () => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('company_id', companyId)
        .order('name');

      if (error) throw error;
      setSuppliers(data || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      // Base categories from inventory types (no "all" here, added separately)
      const baseCategories = [
        { value: 'bottles', label: 'Bottles', type: 'Inventory' },
        { value: 'caps', label: 'Caps', type: 'Inventory' },
        { value: 'labels', label: 'Labels', type: 'Inventory' },
        { value: 'raw_materials', label: 'Chemicals', type: 'Inventory' },
        { value: 'supplies', label: 'Supplies', type: 'Inventory' },
        { value: 'products', label: 'Products', type: 'Inventory' }
      ];

      // Fetch expense categories
      const { data, error } = await supabase
        .rpc('get_expense_categories_decrypted', { company_id_param: companyId });

      if (error) {
        console.error('Error fetching expense categories:', error);
        setCategories(baseCategories);
        return;
      }

      // Add expense categories to the list
      const expenseCategories = (data || []).map(cat => ({
        value: `expense_${cat.id}`,
        label: cat.name,
        type: 'Expense',
        id: cat.id
      }));

      setCategories([...baseCategories, ...expenseCategories]);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to base categories
      setCategories([
        { value: 'bottles', label: 'Bottles', type: 'Inventory' },
        { value: 'caps', label: 'Caps', type: 'Inventory' },
        { value: 'labels', label: 'Labels', type: 'Inventory' },
        { value: 'raw_materials', label: 'Chemicals', type: 'Inventory' },
        { value: 'supplies', label: 'Supplies', type: 'Inventory' },
        { value: 'products', label: 'Products', type: 'Inventory' }
      ]);
    }
  };

  const calculateSummaryStats = (payablesData) => {
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

    const stats = {
      totalOutstanding: 0,
      unpaidCount: 0,
      overdueAmount: 0,
      overdueCount: 0,
      dueNext30Days: 0,
      dueNext30DaysCount: 0,
      totalPaid: 0,
      paidCount: 0
    };

    payablesData.forEach(payable => {
      if (payable.payment_status === 'paid') {
        stats.totalPaid += payable.total_amount;
        stats.paidCount++;
      } else {
        stats.totalOutstanding += payable.remaining_amount;
        stats.unpaidCount++;

        // Check if overdue
        if (payable.invoice_due_date) {
          const dueDate = new Date(payable.invoice_due_date);
          if (dueDate < today) {
            stats.overdueAmount += payable.remaining_amount;
            stats.overdueCount++;
          } else if (dueDate <= thirtyDaysFromNow) {
            stats.dueNext30Days += payable.remaining_amount;
            stats.dueNext30DaysCount++;
          }
        }
      }
    });

    setSummaryStats(stats);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const exportToCSV = () => {
    // Prepare CSV data
    const headers = ['Vendor Name', 'Invoice Number', 'Invoice Date', 'Due Date', 'Total Amount', 'Paid Amount', 'Balance Due', 'Payment Status', 'Category'];
    
    const rows = filteredPayables.map(payable => [
      payable.supplier_name || 'Unknown',
      payable.invoice_number || 'N/A',
      payable.invoice_date || 'N/A',
      payable.invoice_due_date || 'N/A',
      `$${(payable.total_amount || 0).toFixed(2)}`,
      `$${(payable.paid_amount || 0).toFixed(2)}`,
      `$${(payable.remaining_amount || 0).toFixed(2)}`,
      (payable.payment_status || 'unpaid').toUpperCase(),
      payable.transaction_type === 'raw_materials' ? 'CHEMICALS' : (payable.transaction_type || '').replace('_', ' ').toUpperCase()
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payables_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    document.title = `Payables-${new Date().toISOString().split('T')[0]}`;
    
    try {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      
      const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount || 0);
      };

      const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      };

      const totalOutstanding = filteredPayables.reduce((sum, p) => sum + (p.remaining_amount || 0), 0);
      const totalPaid = filteredPayables.reduce((sum, p) => sum + (p.paid_amount || 0), 0);

      const getStatusColor = (status) => {
        if (status === 'paid') return { bg: '#e8f5e9', color: '#2e7d32', border: '#4caf50' };
        if (status === 'partial') return { bg: '#fff3e0', color: '#e65100', border: '#ff9800' };
        return { bg: '#ffebee', color: '#c62828', border: '#f44336' };
      };

      const printableContent = `
        <div style="padding: 24px; backgroundColor: #fff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;">
          <!-- Header -->
          <div style="padding-bottom: 24px; margin-bottom: 32px; border-bottom: 1px solid #e0e0e0;">
            <div style="font-size: 24px; font-weight: 400; letter-spacing: -0.3px; margin-bottom: 6px; color: #000;">GlowWorks</div>
            <div style="font-size: 16px; font-weight: 400; color: #424242; margin-bottom: 6px;">Accounts Payable Report</div>
            <div style="font-size: 11px; color: #757575; font-weight: 400;">
              Generated ${new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
          
          <!-- Summary Cards -->
          <div style="margin-bottom: 40px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px;">
            <div style="padding: 20px; background: #fafafa; border-radius: 8px; border: 1px solid #e0e0e0; text-align: center;">
              <div style="font-size: 11px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.5px; color: #757575; margin-bottom: 8px;">
                Total Outstanding
              </div>
              <div style="font-size: 22px; font-weight: 400; color: #000;">
                ${formatCurrency(totalOutstanding)}
              </div>
            </div>
            <div style="padding: 20px; background: #fafafa; border-radius: 8px; border: 1px solid #e0e0e0; text-align: center;">
              <div style="font-size: 11px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.5px; color: #757575; margin-bottom: 8px;">
                Total Paid
              </div>
              <div style="font-size: 22px; font-weight: 400; color: #000;">
                ${formatCurrency(totalPaid)}
              </div>
            </div>
            <div style="padding: 20px; background: #fafafa; border-radius: 8px; border: 1px solid #e0e0e0; text-align: center;">
              <div style="font-size: 11px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.5px; color: #757575; margin-bottom: 8px;">
                Invoice Count
              </div>
              <div style="font-size: 22px; font-weight: 400; color: #000;">
                ${filteredPayables.length}
              </div>
            </div>
          </div>
          
          <!-- Table -->
          <div style="margin-top: 32px;">
            <div style="font-size: 13px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 16px; color: #424242;">
              Invoice Details
            </div>
            <table style="width: 100%; border-collapse: collapse; font-size: 11px; background: #fff; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden;">
              <thead style="background: #fafafa; border-bottom: 1px solid #e0e0e0;">
                <tr>
                  <th style="padding: 12px; text-align: left; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Vendor</th>
                  <th style="padding: 12px; text-align: left; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Item</th>
                  <th style="padding: 12px; text-align: left; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Invoice #</th>
                  <th style="padding: 12px; text-align: left; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Issued</th>
                  <th style="padding: 12px; text-align: left; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Due Date</th>
                  <th style="padding: 12px; text-align: right; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Total</th>
                  <th style="padding: 12px; text-align: right; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Paid</th>
                  <th style="padding: 12px; text-align: right; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Balance</th>
                  <th style="padding: 12px; text-align: center; font-weight: 400; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; color: #757575;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${filteredPayables.map((payable, index) => {
                  const status = payable.payment_status || 'unpaid';
                  const statusColors = getStatusColor(status);
                  return `
                  <tr style="border-bottom: ${index === filteredPayables.length - 1 ? 'none' : '1px solid #f5f5f5'};">
                    <td style="padding: 14px 12px; vertical-align: top; font-weight: 400; color: #000;">${payable.supplier_name || 'Unknown Vendor'}</td>
                    <td style="padding: 14px 12px; vertical-align: top; font-weight: 400; color: #424242;">${payable.item_name || 'N/A'}</td>
                    <td style="padding: 14px 12px; vertical-align: top; font-weight: 400; color: #424242;">${payable.invoice_number || 'N/A'}</td>
                    <td style="padding: 14px 12px; vertical-align: top; font-weight: 400; color: #424242;">${formatDate(payable.invoice_date)}</td>
                    <td style="padding: 14px 12px; vertical-align: top; font-weight: 400; color: #424242;">${formatDate(payable.invoice_due_date)}</td>
                    <td style="padding: 14px 12px; vertical-align: top; text-align: right; font-weight: 400; font-variant-numeric: tabular-nums; white-space: nowrap; color: #000;">${formatCurrency(payable.total_amount)}</td>
                    <td style="padding: 14px 12px; vertical-align: top; text-align: right; font-weight: 400; font-variant-numeric: tabular-nums; white-space: nowrap; color: #424242;">${formatCurrency(payable.paid_amount)}</td>
                    <td style="padding: 14px 12px; vertical-align: top; text-align: right; font-weight: 400; font-variant-numeric: tabular-nums; white-space: nowrap; color: #000;">${formatCurrency(payable.remaining_amount)}</td>
                    <td style="padding: 14px 12px; vertical-align: top; text-align: center;">
                      <span style="display: inline-block; padding: 4px 10px; background-color: ${statusColors.bg}; color: ${statusColors.color}; border: 1px solid ${statusColors.border}; border-radius: 4px; font-size: 9px; font-weight: 400; text-transform: uppercase; letter-spacing: 0.5px;">
                        ${status.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                `;
                }).join('')}
              </tbody>
            </table>
          </div>

          <!-- Summary Footer -->
          <div style="margin-top: 40px; padding-top: 24px; border-top: 1px solid #e0e0e0;">
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; max-width: 600px; margin-left: auto;">
              <div style="display: flex; justify-content: space-between; padding: 12px 0;">
                <span style="font-size: 12px; font-weight: 400; color: #757575;">Subtotal (Invoices):</span>
                <span style="font-size: 12px; font-weight: 400; color: #000;">${filteredPayables.length}</span>
              </div>
              <div style="display: flex; justify-content: space-between; padding: 12px 0;">
                <span style="font-size: 12px; font-weight: 400; color: #757575;">Total Invoice Amount:</span>
                <span style="font-size: 12px; font-weight: 400; color: #000;">${formatCurrency(filteredPayables.reduce((sum, p) => sum + (p.total_amount || 0), 0))}</span>
              </div>
              <div style="display: flex; justify-content: space-between; padding: 12px 0;">
                <span style="font-size: 12px; font-weight: 400; color: #757575;">Total Paid:</span>
                <span style="font-size: 12px; font-weight: 400; color: #2e7d32;">${formatCurrency(totalPaid)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; padding: 12px 0; border-top: 1px solid #e0e0e0;">
                <span style="font-size: 14px; font-weight: 400; color: #000;">Amount Owed:</span>
                <span style="font-size: 14px; font-weight: 400; color: #c62828;">${formatCurrency(totalOutstanding)}</span>
              </div>
            </div>
          </div>
        </div>
      `;

      const printHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Accounts Payable ${new Date().toLocaleDateString()}</title>
            <style>
              @page { 
                size: auto; 
                margin: 0.3in; 
              }
              @media print {
                body { 
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
              body {
                margin: 0;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
                line-height: 1.3;
                letter-spacing: 0.01071em;
                background: white;
              }
            </style>
          </head>
          <body>
            ${printableContent}
          </body>
        </html>
      `;

      
      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        
        // Close the print window after printing
        setTimeout(() => {
          printWindow.close();
        }, 1000);
      };
      
    } catch (error) {
      console.error('Print error:', error);
      setSnackbar({
        open: true,
        message: 'Error printing report. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleViewDetails = (payable) => {
    setSelectedPayable(payable);
    setDetailDialogOpen(true);
  };

  const handleMarkPayment = (payable) => {
    if (!canEditAccountingPayables()) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to mark payments.',
        severity: 'error'
      });
      return;
    }
    setSelectedPayable(payable);
    setPaymentDialogOpen(true);
  };

  const handleMarkUnpaid = (payable) => {
    if (!canEditAccountingPayables()) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to reverse payments.',
        severity: 'error'
      });
      return;
    }
    setSelectedPayable(payable);
    setUnpaidDialogOpen(true);
  };

  const handlePaymentSuccess = () => {
    setSnackbar({
      open: true,
      message: 'Payment marked successfully',
      severity: 'success'
    });
    fetchPayables();
  };

  const handleUnpaidSuccess = () => {
    setSnackbar({
      open: true,
      message: 'Payment reversed successfully',
      severity: 'success'
    });
    fetchPayables();
  };

  const handleRefresh = () => {
    fetchData();
    setSnackbar({
      open: true,
      message: 'Data refreshed',
      severity: 'success'
    });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4, pb: 10 }}>
      <HeaderCard sx={{ mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center" sx={{ p: 4 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.03)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          >
            <CreditScoreIcon sx={{ fontSize: 36, color: theme.palette.primary.main }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 400, color: 'primary.main', mb: 0.5 }}>
              Accounts Payable
            </Typography>
            <Typography variant="subtitle1" sx={{ 
              color: 'text.secondary', 
              fontWeight: 500,
              fontSize: '1rem',
              mb: 1
            }}>
              {companyName}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track and manage all vendor invoices and payments.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
              sx={{ 
                borderRadius: 2,
                '& .MuiToggleButton-root': {
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2.5,
                  py: 1,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                  '& + .MuiToggleButton-root': {
                    marginLeft: 1.5
                  },
                  '&.Mui-selected': {
                    background: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.15)
                    }
                  }
                }
              }}
            >
              <ToggleButton value="table">
                <ViewListIcon sx={{ mr: 1, fontSize: 20 }} />
                Table
              </ToggleButton>
              <ToggleButton value="calendar">
                <CalendarMonthIcon sx={{ mr: 1, fontSize: 20 }} />
                Calendar
              </ToggleButton>
            </ToggleButtonGroup>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />} 
              onClick={handleRefresh}
              disabled={loading}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 400 }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </HeaderCard>

      {loading ? (
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2
              }}>
                <CreditScoreIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              color: 'primary.main',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Payables
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing vendor invoices and payment data...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          <PayablesSummary summaryStats={summaryStats} loading={loading} />

          {/* Filters */}
          <Box sx={{ mb: 3, mt: 3 }}>
            <PayablesFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              suppliers={suppliers}
              categories={categories}
              summaryStats={summaryStats}
              companyId={companyId}
            />
          </Box>

          {/* Payables View (Table or Calendar) */}
          {viewMode === 'table' && (
            <Box sx={{ mb: 3, mt: 4 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {filteredPayables.length} invoice{filteredPayables.length !== 1 ? 's' : ''}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={exportToCSV}
                    disabled={filteredPayables.length === 0}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Export CSV
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    disabled={filteredPayables.length === 0}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Print
                  </Button>
                </Stack>
              </Stack>
              <PayablesTable
                payables={filteredPayables}
                onViewDetails={handleViewDetails}
                onMarkPayment={handleMarkPayment}
                loading={loading}
                canEdit={canEditAccountingPayables()}
                canDelete={canDeleteAccountingPayables()}
              />
            </Box>
          )}

          {viewMode === 'calendar' && (
            <Box sx={{ mb: 3, mt: 5 }}>
              <PayablesCalendar
                payables={filteredPayables}
                onViewDetails={handleViewDetails}
                canEdit={canEditAccountingPayables()}
                canDelete={canDeleteAccountingPayables()}
              />
            </Box>
          )}
        </>
      )}

      {/* Detail Dialog */}
      <PayableDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        payable={selectedPayable}
        onMarkPayment={handleMarkPayment}
        onMarkUnpaid={handleMarkUnpaid}
        canEdit={canEditAccountingPayables()}
        canDelete={canDeleteAccountingPayables()}
      />

      {/* Payment Dialog */}
      <MarkPaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        payable={selectedPayable}
        onSuccess={handlePaymentSuccess}
        companyId={companyId}
        canEdit={canEditAccountingPayables()}
      />

      {/* Unpaid Dialog */}
      <MarkUnpaidDialog
        open={unpaidDialogOpen}
        onClose={() => setUnpaidDialogOpen(false)}
        payable={selectedPayable}
        onSuccess={handleUnpaidSuccess}
        companyId={companyId}
        canEdit={canEditAccountingPayables()}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
                    sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AccountingPayablesPage;
